import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import { getSpiffItems } from '../Api/Spiff/SpiffApi';

export interface SpiffItem {
  id: number;
  run: string;
  runStatus: string;
  accountNumber: string;
  accountId: number;
  supplier: string;
  rpmRep: string | null;
  product: string;
  grossCommission: number;
  // Not currently rendered -- the SPIFF column derives from grossCommission -- kept because the BE
  // DTO returns it and callers reading the full API shape (e.g. a future column or export) expect it.
  salesCommission: number;
  allocationPercent: number | null;
  noteForStaff: string | null;
  noteForAgents: string | null;
  type: string;
}

export interface ResponseSpiffRecord {
  entityType: string;
  entityId: string;
  spiffs: SpiffItem[];
}

const spiffSubject = new BehaviorSubject<ResponseSpiffRecord | null>(null);
const spiffLoadingSubject = new BehaviorSubject<boolean>(false);

export const getSpiffSubject = (): BehaviorSubject<ResponseSpiffRecord | null> => spiffSubject;
export const getSpiffLoadingSubject = (): BehaviorSubject<boolean> => spiffLoadingSubject;

// Both subjects above are singletons shared by every caller, so an earlier request that resolves
// after a newer one (e.g. rapid navigation from entity A to B, or a forced refresh racing a remount
// of the *same* entity) must not publish anything -- neither the loading flag nor its own
// (by-then-stale) data. A monotonic id -- not just the entity key -- so two in-flight requests for
// the same entity are also correctly ordered, not just requests for different entities.
let latestRequestId = 0;

export const fetchSpiffItems = async (entityType: string, entityId: string, forceRefresh = false) => {
  const currentValue = spiffSubject.getValue();
  const isSameEntityAlreadyLoaded =
    currentValue && currentValue.entityId === entityId && currentValue.entityType === entityType;
  if (!forceRefresh && isSameEntityAlreadyLoaded) {
    return;
  }

  const requestId = ++latestRequestId;
  spiffLoadingSubject.next(true);

  const { data, error } = await getSpiffItems(entityType, entityId);

  if (requestId !== latestRequestId) {
    return;
  }

  if (data) {
    clearError('spiff');
    spiffSubject.next(data);
  }
  if (error) addError('spiff', error.message);

  spiffLoadingSubject.next(false);
};
